<!-- AlloyLogSourcesSection.vue -->
<template>
  <div class="space-y-6 glass-panel dark:text-bg-gray">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="font-semibold text-gray-800 dark:text-gray-200 capitalize">Self-Monitoring &amp; Logs</h2>
      <div class="relative group">
        <button type="button" class="bg-transparent hover:bg-transparent" @click="$emit('start-tutorial')" aria-label="Section tutorial">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <span class="absolute top-full text-xs left-0 glass-card mb-1 px-1.5 py-0.5 text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>

    <div class="columns-1 lg:columns-2 gap-6">
    <!-- Self-Monitoring (shared) -->
    <div class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <h3 class="font-semibold mb-4 text-gray-600 dark:text-gray-400 capitalize">Agent Self-Monitoring</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="flex flex-col">
          <label for="self_monitoring_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Alloy Self-Monitoring</label>
          <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input v-model="modelValue.self_monitoring_enabled" id="self_monitoring_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="self_monitoring_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
      </div>
    </div>

    <!-- Windows Event Logs -->
    <div v-if="isWindows" class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <h3 class="font-semibold mb-4 text-gray-600 dark:text-gray-400 capitalize">Windows Event Logs</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="flex flex-col">
          <label for="eventlog_system_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">System Event Log</label>
          <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input v-model="modelValue.eventlog_system_enabled" id="eventlog_system_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="eventlog_system_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="eventlog_application_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Application Event Log</label>
          <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input v-model="modelValue.eventlog_application_enabled" id="eventlog_application_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="eventlog_application_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col md:col-span-2">
          <label for="eventlog_xpath_query" class="mb-1 font-medium text-gray-700 dark:text-gray-50">System Log XPath Filter</label>
          <input id="eventlog_xpath_query" v-model="modelValue.eventlog_xpath_query"
            placeholder="*[System[(Level=1 or Level=2 or Level=3)]]"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
    </div>

    <!-- Linux Systemd Journal -->
    <div v-else-if="isLinux" class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <h3 class="font-semibold mb-4 text-gray-600 dark:text-gray-400 capitalize">Systemd Journal</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="flex flex-col">
          <label for="journal_enabled" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Journal Logs</label>
          <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
            <input v-model="modelValue.journal_enabled" id="journal_enabled" type="checkbox"
              class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer peer checked:right-0 checked:bg-blue-600 transition-all duration-300" />
            <label for="journal_enabled" class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"></label>
          </div>
        </div>
        <div class="flex flex-col">
          <label for="journal_max_age" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Max Age</label>
          <input id="journal_max_age" v-model="modelValue.journal_max_age"
            placeholder="e.g. 12h"
            class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
      <div class="mt-4">
        <label class="mb-1 block font-medium text-gray-700 dark:text-gray-50">Filter to Units (Optional)</label>
        <div class="flex items-center space-x-2 mb-2">
          <input v-model="newJournalUnit"
            placeholder="e.g. ssh.service"
            class="flex-1 border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button type="button" @click="addJournalUnit"
            class="px-4 py-2 rounded-lg bg-gradient-to-b from-green-500 to-green-600 text-white text-sm font-medium shadow-md hover:from-green-600 hover:to-green-700 hover:shadow-lg active:shadow-sm active:translate-y-px transition-all duration-200">
            Add
          </button>
        </div>
        <ul class="flex flex-wrap gap-2">
          <li v-for="(unit, index) in modelValue.journal_units" :key="index"
            class="glass-card border-gray-300 dark:border-white/20 px-3 py-1 rounded-md shadow-sm text-sm font-medium">
            <div class="flex justify-between items-center">
              <span class="truncate text-sm text-gray-900 dark:text-gray-50">{{ unit }}</span>
              <button type="button" @click="removeJournalUnit(index)" class="shrink-0 ml-2 bg-transparent hover:bg-transparent focus:bg-transparent text-gray-400 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-500 hover:scale-125 transition-all duration-150">
                <Trash2 />
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Log File Paths -->
    <div class="mb-6 break-inside-avoid p-4 rounded-lg border border-gray-300 dark:border-gray-500 shadow-sm">
      <h3 class="font-semibold mb-4 text-gray-600 dark:text-gray-400 capitalize">Log File Paths</h3>
      <div class="flex items-center space-x-2 mb-2">
        <input v-model="newLogFilePath"
          :placeholder="isLinux ? 'e.g. /var/log/*.log' : 'e.g. C:/Logs/*.log'"
          class="flex-1 border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button type="button" @click="addLogFilePath"
          class="px-4 py-2 rounded-lg bg-gradient-to-b from-green-500 to-green-600 text-white text-sm font-medium shadow-md hover:from-green-600 hover:to-green-700 hover:shadow-lg active:shadow-sm active:translate-y-px transition-all duration-200">
          Add
        </button>
      </div>
      <ul class="flex flex-wrap gap-2">
        <li v-for="(path, index) in modelValue.log_file_paths" :key="index"
          class="glass-card-lite border-gray-300 dark:border-white/20 px-3 py-1 rounded-md shadow-sm text-sm font-medium">
          <div class="flex justify-between items-center">
            <span class="truncate text-sm text-gray-900 dark:text-gray-50">{{ path }}</span>
            <button type="button" @click="removeLogFilePath(index)" class="shrink-0 ml-2 bg-transparent hover:bg-transparent focus:bg-transparent text-gray-400 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-500 hover:scale-125 transition-all duration-150">
              <Trash2 />
            </button>
          </div>
        </li>
      </ul>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Trash2 } from 'lucide-vue-next'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  osType: {
    type: String,
    default: ''
  }
})

defineEmits(['update:modelValue', 'start-tutorial'])

const isWindows = computed(() => props.osType === 'windows')
const isLinux = computed(() => props.osType === 'linux')

const newLogFilePath = ref('')
const newJournalUnit = ref('')

function addLogFilePath() {
  const value = newLogFilePath.value.trim()
  if (value !== '') {
    props.modelValue.log_file_paths.push(value)
    newLogFilePath.value = ''
  }
}

function removeLogFilePath(index) {
  props.modelValue.log_file_paths.splice(index, 1)
}

function addJournalUnit() {
  const value = newJournalUnit.value.trim()
  if (value !== '') {
    props.modelValue.journal_units.push(value)
    newJournalUnit.value = ''
  }
}

function removeJournalUnit(index) {
  props.modelValue.journal_units.splice(index, 1)
}
</script>
